import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { Route, Routes } from 'react-router';
import type { Cfg, EIDCfg, LookupCfg } from '../types/config.js';
import { ToastProvider } from './hooks/useToast.js';
import { EIDVerificationPage } from './pages/eid-verification/index.js';
import { FormPage } from './pages/form.js';
import { VariantSelectionPage, VehicleLookupPage } from './pages/vehicle-lookup/index.js';
import { WizardPage } from './pages/wizard.js';
import { getAppConfig } from './utils/getHuckConfig.js';

const SuperUserAlert = React.lazy(() => import('./components/super-user-alert.js'));

export const App = () => {
  const config = getAppConfig();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
        staleTime: 1000 * 60 * 5,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Routes>
          <Route path="/eid" element={<EIDVerificationPage config={config as EIDCfg} />} />
          {/* Legacy lookup routes (pre-item creation) */}
          <Route path="/lookup" element={<VehicleLookupPage config={config as LookupCfg} />} />
          <Route path="/lookup/:regnr" element={<VariantSelectionPage config={config as LookupCfg} />} />
          <Route path="/lookup/:regnr/:variantId" element={<VariantSelectionPage config={config as LookupCfg} />} />
          {/* Item-scoped lookup routes for back navigation (TAI-1899) */}
          <Route path="/item/:itemId/lookup" element={<VehicleLookupPage config={config as LookupCfg} />} />
          <Route path="/item/:itemId/lookup/:regnr" element={<VariantSelectionPage config={config as LookupCfg} />} />
          <Route
            path="/item/:itemId/lookup/:regnr/:variantId"
            element={<VariantSelectionPage config={config as LookupCfg} />}
          />
          {/* Item insertion wizard route - only available when ad model is "wizard ready" */}
          {(config as Cfg).isWizardModel ? (
            <Route path={`/item/${(config as Cfg).itemId}/steps/:step?`} element={<WizardPage />} />
          ) : (
            <Route path={`/item/${(config as Cfg).itemId}?`} element={<FormPage />} />
          )}
        </Routes>
        {(config as Cfg).isSuperUser ? (
          <React.Suspense fallback={null}>
            <SuperUserAlert />
          </React.Suspense>
        ) : null}
      </ToastProvider>
    </QueryClientProvider>
  );
};
